import React from "react";
import { Timestamp } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import LinedButton from "components/token/LinedButton";
import { Entity, Video } from "../../types/cms";
import { firstLogo } from "../../lib/catalog";

interface VideoHeaderProps {
  video: Video;
  formatDate: (timestamp: Timestamp) => string;
  agency: Entity | null;
  company: Entity | null;
}

const VideoHeader: React.FC<VideoHeaderProps> = ({
  video,
  formatDate,
  agency,
  company,
}) => {
  const navigate = useNavigate();

  // Build the header items using the separate agency and company props
  const headerItems = [
    {
      title: "Réalisation",
      value: agency?.name || "In house",
      link: agency?.id ? `/studio/${agency.id}` : undefined,
    },
    // {
    //   title: "Case Study",
    //   value: "Explore →",
    // },
    {
      title: "Industry",
      value: video?.industries?.[0] || "App",
    },
    // {
    //   title: "Assets",
    //   value: "Download",
    // },
    {
      title: "Company",
      value: "Website →",
      link: company?.websiteURL,
    },
  ];

  // Helper function to handle navigation clicks
  const handleNavigation = (link?: string) => {
    if (!link) return;

    // Check if the link ends with a file extension (like .pdf, .doc, etc.)
    const endsWithExtension = /\.[a-z]+$/i.test(link);

    // If the link does not start with a protocol and appears to be a domain,
    // then prepend "https://"
    if (
      !link.startsWith("http://") &&
      !link.startsWith("https://") &&
      link.includes(".") &&
      !link.startsWith("/")
    ) {
      link = `https://${link}`;
    }

    // If the link is external or ends with an extension, open it in a new tab.
    if (
      link.startsWith("http://") ||
      link.startsWith("https://") ||
      endsWithExtension
    ) {
      window.open(link, "_blank");
    } else {
      // Otherwise, navigate using react-router.
      navigate(link);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* -------- Top Title + Brand Info + Date -------- */}
      <header className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-8 md:gap-16">
        {/* Left - Big Title */}
        <h1 className="text-5xl font-bold mb-2 md:mb-0 max-w-max w-full">
          {video?.title || "Titre de la vidéo"}
        </h1>

        {/* Right - Brand Description & Date */}
        <Link
          className="flex items-center gap-6 max-w-xl"
          to={company?.id ? `/brand/${company.id}` : "#"}
        >
          {firstLogo(company) && (
            <img
              src={firstLogo(company)}
              alt={`${company?.name} logo`}
              className="h-10 w-10 object-contain rounded-lg"
            />
          )}
          <div className="text-sm">
            <h6 className="font-semibold">{company?.name}</h6>
            {company?.description && (
              <p className="text-gray-500 text-xs">{company.description}</p>
            )}
          </div>
        </Link>
      </header>

      <div className="flex justify-between grow">
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-8 p-6 bg-gray-100 rounded-2xl w-full">
          {headerItems.map((item, index) => (
            <div key={index} className="flex flex-col gap-1 sm-gap-3">
              <p className="text-base text-black font-semibold">{item.title}</p>
              <LinedButton onClick={() => handleNavigation(item.link)}>
                {item.value}
              </LinedButton>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoHeader;
